/**
 * MyGamesScreen — рабочее место автора.
 *
 * Самая большая дыра старого интерфейса: раздела для создателей не было
 * вообще. При этом данные есть — у активного автора сотни черновиков
 * против десятков опубликованных, и в каждой записи лежит `status`,
 * `quality_state`, `moderator_comment`. То есть система знала, что игра
 * отклонена и почему, а показать это было негде.
 *
 * При переводе на дизайн-систему:
 *  • кнопка «Создать в Студии» меняла текст на «Открываем…» и от этого
 *    меняла ширину — курсор промахивался мимо неё. Теперь состояние
 *    loading: ширина фиксирована, спиннер поверх;
 *  • ошибка открытия Студии писалась в тот же слот, что и ошибка загрузки
 *    списка. Из-за этого «не удалось открыть Студию» выглядело как «твои
 *    игры не загрузились». Теперь это разные состояния;
 *  • комментарии модератора были плоскими строками — теперь карточки
 *    с явным адресатом: без этого автор не понимал, к какой игре относится
 *    текст, если отклонённых несколько.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import * as cabinetApi from "../../../api/cabinet";
import { STUDIO_BASE, requestPlayTicket } from "../../../api/playTicket";
import GameCard, { GameCardSkeleton } from "../../game/GameCard";
import CabIcon from "../CabIcon";
import { Button, Card, Pill, Tabs, EmptyState, Stat, fmtCompact, fmtNum } from "../../ui";
import "../../../styles/cabinet-mygames.css";

/* Порядок вкладок = порядок работы автора: сначала общее, потом готовое,
   потом то, что в работе, и в конце требующее внимания. */
const TABS = [
  { id: "all", label: "Все" },
  { id: "published", label: "Опубликованы" },
  { id: "pending", label: "На проверке" },
  { id: "draft", label: "Черновики" },
  { id: "rejected", label: "Отклонены" },
];

function matchTab(game, tab) {
  const s = game?.status;
  if (tab === "all") return true;
  if (tab === "rejected") return s === "blocked" || s === "rejected";
  return s === tab;
}

export default function MyGamesScreen() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [error, setError] = useState(null);
  const [studioError, setStudioError] = useState(null);
  const [openingStudio, setOpeningStudio] = useState(false);

  const loadGames = React.useCallback(
    (signal) =>
      cabinetApi.getMyProjects(user.userId, signal).then((list) => {
        // Свежие сверху: автор работает с последним, а не с первым.
        const sorted = [...list].sort((a, b) =>
          String(b.updated_at || b.created_at || "").localeCompare(
            String(a.updated_at || a.created_at || "")
          )
        );
        setGames(sorted);
      }),
    [user?.userId]
  );

  useEffect(() => {
    if (!isAuthenticated || !user?.userId) {
      setLoading(false);
      return undefined;
    }
    const ctrl = new AbortController();
    let cancelled = false;

    setLoading(true);
    setError(null);
    loadGames(ctrl.signal)
      .catch((e) => {
        if (e?.name !== "AbortError" && !cancelled) {
          setError("Не удалось загрузить твои игры");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [isAuthenticated, user?.userId, loadGames]);

  const counts = useMemo(() => {
    const c = {};
    for (const t of TABS) c[t.id] = games.filter((g) => matchTab(g, t.id)).length;
    return c;
  }, [games]);

  const totals = useMemo(() => {
    const published = games.filter((g) => g.status === "published");
    return {
      plays: published.reduce((s, g) => s + (Number(g.play_count) || 0), 0),
      likes: published.reduce((s, g) => s + (Number(g.likes_count) || 0), 0),
      published: published.length,
    };
  }, [games]);

  const visible = games.filter((g) => matchTab(g, tab));

  /* Переход в Студию — через одноразовый SSO-тикет, иначе человек
     окажется на чужом домене разлогиненным. */
  const openStudio = async () => {
    if (openingStudio) return;
    setOpeningStudio(true);
    setStudioError(null);
    // Вкладку открываем СИНХРОННО, до await: иначе браузер сочтёт
    // её всплывающей и заблокирует.
    const win = window.open("about:blank", "_blank");
    try {
      const ticket = await requestPlayTicket();
      const url = `${STUDIO_BASE}/#ticket=${encodeURIComponent(ticket)}`;
      if (win) {
        win.location.replace(url);
        win.opener = null;
      } else {
        window.location.assign(url);
      }
    } catch {
      if (win) win.close();
      setStudioError("Не получилось открыть Студию. Попробуй ещё раз.");
    } finally {
      setOpeningStudio(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="ui-page">
        <EmptyState
          icon={<CabIcon name="puzzle" size={40} />}
          title="Здесь живут твои игры"
          desc="Войди, чтобы создавать игры и следить за их статистикой."
          action={
            <Button variant="primary" onClick={() => navigate("/login")}>
              Войти
            </Button>
          }
        />
      </div>
    );
  }

  /* Вкладки со счётчиками: автор должен видеть, сколько игр ждёт
     проверки и сколько отклонено, не открывая каждую вкладку. */
  const tabItems = TABS.map((t) => ({
    id: t.id,
    label: counts[t.id] > 0 ? `${t.label} · ${counts[t.id]}` : t.label,
  }));

  return (
    <div className="ui-page">
      <div className="rb-my__head">
        <div className="rb-my__head-text">
          <h1 className="rb-my__title">Мои игры</h1>
          <p className="rb-my__sub">
            Черновики, игры на проверке и всё, что уже играют.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={openStudio}
          loading={openingStudio}
          icon={<CabIcon name="rocket" size={17} />}
        >
          Создать в Студии
        </Button>
      </div>

      {studioError && (
        <Card accent className="rb-my__alert">
          <CabIcon name="warning" size={19} />
          <span>{studioError}</span>
          <Button variant="ghost" size="sm" onClick={openStudio}>
            Повторить
          </Button>
        </Card>
      )}

      {/* Сводка по опубликованным: автору важно видеть отдачу, а не
          только список файлов. */}
      {totals.published > 0 && (
        <div className="rb-my__totals">
          <Stat value={fmtNum(totals.published)} label="опубликовано" />
          <Stat value={fmtCompact(totals.plays)} label="запусков" />
          <Stat value={fmtCompact(totals.likes)} label="лайков" />
        </div>
      )}

      <Tabs items={tabItems} value={tab} onChange={setTab} className="rb-my__tabs" />

      {error ? (
        <EmptyState
          icon={<CabIcon name="warning" size={40} />}
          title="Не получилось загрузить"
          desc="Проверь соединение и попробуй ещё раз."
          action={
            <Button
              variant="primary"
              onClick={() => {
                setError(null);
                setLoading(true);
                loadGames()
                  .catch(() => setError("Не удалось загрузить твои игры"))
                  .finally(() => setLoading(false));
              }}
            >
              Повторить
            </Button>
          }
        />
      ) : loading ? (
        <div className="rb-gcard-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <GameCardSkeleton key={i} />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<CabIcon name="puzzle" size={40} />}
          title={tab === "all" ? "Здесь появятся твои игры" : "В этой категории пусто"}
          desc={
            tab === "all"
              ? "Создай первую в Студии — это бесплатно и без установки."
              : "Переключись на другую вкладку или создай новую игру."
          }
          action={
            tab === "all" ? (
              <Button variant="primary" onClick={openStudio} loading={openingStudio}>
                Открыть Студию
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setTab("all")}>
                Показать все
              </Button>
            )
          }
        />
      ) : (
        <>
          {/* Комментарий модератора — единственное место, где автор узнаёт
              причину отказа. Над сеткой, чтобы не потерялся среди карточек. */}
          {tab === "rejected" && visible.some((g) => g.moderator_comment) && (
            <div className="rb-my__notes">
              {visible
                .filter((g) => g.moderator_comment)
                .map((g) => (
                  <Card key={g.id} className="rb-my__note">
                    <div className="rb-my__note-head">
                      <Pill tone="danger">Отклонена</Pill>
                      <b className="rb-my__note-title">{g.title}</b>
                    </div>
                    <p className="rb-my__note-text">{g.moderator_comment}</p>
                  </Card>
                ))}
            </div>
          )}

          <div className="rb-gcard-grid">
            {visible.map((g) => (
              <GameCard
                key={g.id}
                game={g}
                showStatus
                onClick={(id) => navigate(`/app/game/${id}`)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
